// STEP LIVE-STUDIO-CONSOLE-SPLIT-3 · Shared Quick Register modal.
//
// 목적: Studio (SellerLiveControl) 에서만 노출되던 QuickAddModal 을 shared
// component 로 추출.  Studio + Console 양쪽이 import 하여 **동일 UI + 동일
// canonical action (ops.quickRegister via useLiveOperations)** 를 사용한다.
//
// Canonical contract (실제 backend 확인):
//   · POST /seller/broadcasts/{sid}/quick-product
//        body: {name, sale_price, image?, stock?, options?, announce_now?}
//        → {product, slot_number, announced}
//
// 금지 (STEP §3):
//   · 신규 Product Master · 신규 variant · 신규 inventory 경로.
//   · api.post(/quick-product) 직접 호출 (반드시 ops.quickRegister 경유).
//
// Props:
//   open         — modal visibility (boolean)
//   onClose      — 닫기 handler (() => void)
//   scheduleId   — 소속 broadcast schedule id (`schedule_xxx`)
//   onCreated    — 등록 성공 시 product list refresh 콜백 (shell 쪽 loadDetail /
//                  loadLiveCtx trigger).  Studio/Console 양쪽에서 동일 contract.
//   STEP STUDIO-MOBILE-QUICK-REGISTER-1 (2026-10-12) · optional props:
//     initialImageFile — 외부(rail 자동 캡처) 로부터 전달받은 File. open 전환 시 imgFile 로 반영.
//     isTestMode       — true 면 /quick-product / /uploads 호출 skip.  대신 onTestRegister 사용.
//     onTestRegister   — (body) => Promise<{product, slot_number, announced}> · 가상 등록 결과.
//
// Voice recording / voice command / option group 편집 등 내부 UX 는 Studio 기존
// 구현 100% 보존 — 파일 이동 외 로직 변경 없음 (내부 /quick-product → ops 교체 1곳).

import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Zap, Mic, Loader2, Square, Camera, Upload, X, Check, Plus } from 'lucide-react';
import api from '@/lib/api';
import useLiveOperations from '@/hooks/useLiveOperations';

const QuickAddModal = ({ open, onClose, scheduleId, onCreated,
                         initialImageFile = null, isTestMode = false, onTestRegister = null, defaultAnnounce = true, uploadInTest = false }) => {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [stock, setStock] = useState('');
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState('');
  const [imgUrl, setImgUrl] = useState('');
  const [announceNow, setAnnounceNow] = useState(defaultAnnounce);
  // QUICK-REGISTER-1 · 보조자 캡처 등록은 현재 소개 상품을 자동 변경하지 않음(defaultAnnounce=false).
  useEffect(() => { if (open) setAnnounceNow(defaultAnnounce); }, [open, defaultAnnounce]);
  const [hasOptions, setHasOptions] = useState(false);
  // Up to 2 option groups (색상, 사이즈). Each group has name + list of values
  // where each value has {label, extra_price}. Matches seller_products.options.
  const [optionGroups, setOptionGroups] = useState([
    { name: '색상', values: [{ label: '', extra_price: 0 }] },
  ]);
  const [busy, setBusy] = useState(false);
  // STEP LIVE-STUDIO-CONSOLE-SPLIT-3 · canonical quickRegister (ops) REUSE.
  const ops = useLiveOperations(scheduleId);

  // STEP STUDIO-MOBILE-QUICK-REGISTER-1 · 외부(rail) 자동 캡처 File 반영.
  //   · open 이 true 로 바뀌는 순간 + file 변경 시 imgFile/preview 를 교체.
  //   · 사용자가 재촬영/교체하면 그 상태는 modal 내부가 소유 (파일 input 으로 덮어씀).
  useEffect(() => {
    if (!open || !initialImageFile) return;
    // 기존 blob preview URL 회수 (메모리 누수 방지).
    try { if (imgPreview && imgPreview.startsWith('blob:')) URL.revokeObjectURL(imgPreview); } catch {}
    setImgFile(initialImageFile);
    setImgPreview(URL.createObjectURL(initialImageFile));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialImageFile]);
  // STEP 6-VOICE-C · 음성 입력 (MediaRecorder → /seller/live/voice-quick-parse → QuickAddModal state 자동채움)
  const [voiceState, setVoiceState] = useState('idle'); // 'idle' | 'recording' | 'processing' | 'error'
  const [voiceMsg, setVoiceMsg] = useState('');
  // STEP 6-VOICE-D · 자동채움 결과 확인 요약 · 가격 확인 hint
  const [voiceFilled, setVoiceFilled] = useState(null); // { name, sale_price, stock, options: [] }
  // STEP 6-VOICE-F · 연속 상품 등록 완료 상태 + 음성 명령
  const [justCreated, setJustCreated] = useState(null); // { name, slot_number, announced }
  const [cmdState, setCmdState] = useState('idle'); // 'idle' | 'listening' | 'processing'
  const [cmdMsg, setCmdMsg] = useState('');
  const mediaRecRef = React.useRef(null);
  const audioChunksRef = React.useRef([]);
  const cmdRecRef = React.useRef(null);
  const cmdChunksRef = React.useRef([]);
  const cmdTimerRef = React.useRef(null);
  const startVoice = async () => {
    if (voiceState !== 'idle') return;
    setVoiceMsg('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '');
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) audioChunksRef.current.push(ev.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: rec.mimeType || 'audio/webm' });
        audioChunksRef.current = [];
        setVoiceState('processing');
        try {
          const fd = new FormData();
          const ext = (rec.mimeType || '').includes('mp4') ? 'mp4' : 'webm';
          fd.append('audio', blob, `voice.${ext}`);
          const r = await api.post('/seller/live/voice-quick-parse', fd, {
            headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000,
          });
          const parsed = r.data?.parsed;
          if (!parsed) {
            setVoiceState('error');
            setVoiceMsg(r.data?.message || '음성은 인식했지만 상품정보 추출 실패. 수동 입력해주세요.');
            toast.error('상품정보 추출 실패');
            return;
          }
          if (parsed.name) setName(parsed.name);
          if (parsed.sale_price != null) setPrice(String(parsed.sale_price));
          if (parsed.stock != null) setStock(String(parsed.stock));
          // STEP 6-VOICE-I · option_groups (색상/사이즈 자동 분류) 우선 · 레거시 flat options fallback
          if (Array.isArray(parsed.option_groups) && parsed.option_groups.length > 0) {
            setHasOptions(true);
            setOptionGroups(parsed.option_groups.slice(0, 2).map((g) => ({
              name: g.name || '옵션',
              values: (g.values || []).map((v) => ({ label: String(v), extra_price: 0 })),
            })));
          } else if (Array.isArray(parsed.options) && parsed.options.length > 0) {
            setHasOptions(true);
            setOptionGroups([{ name: '옵션', values: parsed.options.map((label) => ({ label: String(label), extra_price: 0 })) }]);
          }
          setVoiceFilled(parsed); // STEP 6-VOICE-D · 요약 표시 + 가격 hint
          setVoiceState('idle');
          setVoiceMsg('');
          toast.success('음성 입력을 반영했습니다. 내용을 확인 후 등록해주세요.');
        } catch (e) {
          setVoiceState('error');
          const msg = e?.response?.data?.detail || '음성을 정확히 인식하지 못했습니다. 다시 말하거나 직접 입력해주세요.';
          setVoiceMsg(msg);
          toast.error(msg);
        }
      };
      mediaRecRef.current = rec;
      rec.start();
      setVoiceState('recording');
      // Safety timeout: 15초 후 자동 정지 (장시간 녹음 금지 정책)
      setTimeout(() => { try { if (rec.state === 'recording') rec.stop(); } catch(_){} }, 15000);
    } catch (e) {
      setVoiceState('error');
      const msg = e?.name === 'NotAllowedError' ? '마이크 권한을 허용해주세요.' : '마이크를 사용할 수 없습니다.';
      setVoiceMsg(msg); toast.error(msg);
    }
  };
  const stopVoice = () => {
    try { const rec = mediaRecRef.current; if (rec && rec.state === 'recording') rec.stop(); } catch (_) {}
  };
  const reset = () => {
    // STEP 6-VOICE-F · blob object URL 회수 (메모리 누수 방지)
    try { if (imgPreview && imgPreview.startsWith('blob:')) URL.revokeObjectURL(imgPreview); } catch(_){}
    setName(''); setPrice(''); setStock(''); setImgFile(null); setImgPreview('');
    setImgUrl(''); setAnnounceNow(true); setHasOptions(false);
    setOptionGroups([{ name: '색상', values: [{ label: '', extra_price: 0 }] }]);
    setVoiceFilled(null); setVoiceMsg(''); setVoiceState('idle');
    setJustCreated(null); setCmdMsg('');
  };
  // STEP 6-VOICE-F · 연속 등록 · Modal 유지 / 닫기 분기
  const handleNext = () => { reset(); /* Modal 유지 · 다음 상품 입력 준비 */ };
  const handleClose = () => { reset(); onClose(); };
  // STEP 6-VOICE-F · 음성 명령 (confirmation state 또는 done state 에서만 활성)
  const stopCmd = () => {
    try { const rec = cmdRecRef.current; if (rec && rec.state === 'recording') rec.stop(); } catch(_){}
  };
  const startCmd = async (mode /* 'confirm' | 'done' */) => {
    if (cmdState !== 'idle') return;
    setCmdMsg('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '');
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      cmdChunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) cmdChunksRef.current.push(ev.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (cmdTimerRef.current) { clearTimeout(cmdTimerRef.current); cmdTimerRef.current = null; }
        const blob = new Blob(cmdChunksRef.current, { type: rec.mimeType || 'audio/webm' });
        cmdChunksRef.current = [];
        setCmdState('processing');
        try {
          const fd = new FormData();
          const ext = (rec.mimeType || '').includes('mp4') ? 'mp4' : 'webm';
          fd.append('audio', blob, `cmd.${ext}`);
          const r = await api.post('/seller/live/voice-quick-parse', fd, {
            headers: { 'Content-Type': 'multipart/form-data' }, timeout: 15000,
          });
          const t = (r.data?.transcript || '').trim();
          const s = t.replace(/\s+/g, '');
          let action = null;
          if (mode === 'confirm') {
            if (/취소/.test(s)) action = 'cancel';
            else if (/등록.*소개|소개.*등록/.test(s)) action = 'submit_announce';
            else if (/등록만/.test(s)) action = 'submit_only';
            else if (/등록/.test(s)) action = 'submit_default';
          } else if (mode === 'done') {
            if (/다음/.test(s)) action = 'next';
            else if (/닫기|종료|끝/.test(s)) action = 'close';
          }
          setCmdState('idle');
          if (!action) {
            setCmdMsg(`인식된 명령이 없습니다: "${t.slice(0, 30)}"`);
            toast.error('명령을 이해하지 못했습니다.');
            return;
          }
          if (action === 'cancel')          { setVoiceFilled(null); setVoiceMsg(''); toast.info('음성 입력을 취소했습니다.'); return; }
          if (action === 'submit_announce') { toast.success('음성 명령: 등록하고 소개'); await submit({ announceOverride: true }); return; }
          if (action === 'submit_only')     { toast.success('음성 명령: 등록만');       await submit({ announceOverride: false }); return; }
          if (action === 'submit_default')  { toast.success('음성 명령: 등록');         await submit({}); return; }
          if (action === 'next')            { toast.success('음성 명령: 다음 상품');    handleNext(); return; }
          if (action === 'close')           { toast.success('음성 명령: 닫기');         handleClose(); return; }
        } catch (e) {
          setCmdState('idle');
          const msg = e?.response?.data?.detail || '음성 명령 인식 실패';
          setCmdMsg(msg); toast.error(msg);
        }
      };
      cmdRecRef.current = rec;
      rec.start();
      setCmdState('listening');
      // 5초 안전 정지 (명령 짧은 발화만 허용)
      cmdTimerRef.current = setTimeout(() => { try { if (rec.state === 'recording') rec.stop(); } catch(_){} }, 5000);
    } catch (e) {
      setCmdState('idle');
      const msg = e?.name === 'NotAllowedError' ? '마이크 권한을 허용해주세요.' : '마이크를 사용할 수 없습니다.';
      setCmdMsg(msg); toast.error(msg);
    }
  };

  const uploadImage = async () => {
    if (!imgFile) return null;
    const fd = new FormData();
    fd.append('file', imgFile);
    fd.append('kind', 'product');
    try {
      const r = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return r.data?.url || null;
    } catch (e) { toast.error('이미지 업로드 실패'); return null; }
  };

  // ---- option editors ----------------------------------------------------
  const setGroupName = (gi, v) => setOptionGroups((g) => g.map((x, i) => i === gi ? { ...x, name: v } : x));
  const setValue = (gi, vi, key, v) => setOptionGroups((g) => g.map((x, i) => i === gi
    ? { ...x, values: x.values.map((y, j) => j === vi ? { ...y, [key]: v } : y) }
    : x));
  const addValue = (gi) => setOptionGroups((g) => g.map((x, i) => i === gi
    ? { ...x, values: [...x.values, { label: '', extra_price: 0 }] } : x));
  const removeValue = (gi, vi) => setOptionGroups((g) => g.map((x, i) => i === gi
    ? { ...x, values: x.values.filter((_, j) => j !== vi) } : x));
  const addGroup = () => {
    if (optionGroups.length >= 2) { toast.error('옵션 그룹은 최대 2개까지 추가 가능합니다.'); return; }
    setOptionGroups((g) => [...g, { name: '사이즈', values: [{ label: '', extra_price: 0 }] }]);
  };
  const removeGroup = (gi) => setOptionGroups((g) => g.filter((_, i) => i !== gi));

  // Sanitise the option groups before sending. Empty names/labels are dropped;
  // if nothing usable is left we send an empty [] (i.e. treat as no options).
  const cleanOptions = () => {
    if (!hasOptions) return [];
    return optionGroups
      .map((g) => ({
        name: (g.name || '').trim(),
        values: (g.values || [])
          .map((v) => ({ label: (v.label || '').trim(), extra_price: Number(v.extra_price || 0) }))
          .filter((v) => v.label),
      }))
      .filter((g) => g.name && g.values.length > 0);
  };

  const submit = async ({ announceOverride } = {}) => {
    if (!name.trim()) { toast.error('상품명을 입력하세요.'); return false; }
    const p = Number(price);
    if (!Number.isFinite(p) || p < 0) { toast.error('판매가를 올바르게 입력하세요.'); return false; }
    const opts = cleanOptions();
    if (hasOptions && opts.length === 0) {
      toast.error('옵션 있음을 선택했다면 옵션값을 하나 이상 입력하세요.'); return false;
    }
    setBusy(true);
    try {
      let image = imgUrl.trim() || null;
      if (imgFile) {
        if (isTestMode && !uploadInTest) {
          // STEP STUDIO-MOBILE-QUICK-REGISTER-1 · 테스트 모드는 서버 업로드 skip.
          //   blob preview URL 을 그대로 image 로 전달 (파기 전까지 로컬 렌더 가능).
          image = imgPreview || image;
        } else {
          image = await uploadImage() || image;
        }
      }
      const useAnnounce = (typeof announceOverride === 'boolean') ? announceOverride : announceNow;
      const body = {
        name: name.trim(), sale_price: Math.floor(p), image,
        stock: stock === '' ? undefined : Number(stock),
        options: opts,
        announce_now: useAnnounce,
      };
      // STEP STUDIO-MOBILE-QUICK-REGISTER-1 · 테스트 모드 분기 — WRITE 0.
      const r = { data: isTestMode && onTestRegister
        ? await onTestRegister(body)
        : await ops.quickRegister(body) };
      const slot = r.data.slot_number;
      const announced = !!r.data.announced;
      const createdName = r.data.product?.name || name.trim();
      toast.success(`"${createdName}" 등록 완료 (슬롯 ${slot}${announced ? ', 소개중' : ''})`);
      onCreated?.(r.data.product || null);
      // STEP 6-VOICE-F · Modal 닫지 않음 · 완료 상태 진입 (판매자가 [다음] 또는 [닫기] 선택)
      // 폼 필드는 안전 초기화 (다음 상품 대비) · justCreated 배너 유지
      try { if (imgPreview && imgPreview.startsWith('blob:')) URL.revokeObjectURL(imgPreview); } catch(_){}
      setName(''); setPrice(''); setStock('');
      setImgFile(null); setImgPreview(''); setImgUrl('');
      setAnnounceNow(true); setHasOptions(false);
      setOptionGroups([{ name: '색상', values: [{ label: '', extra_price: 0 }] }]);
      setVoiceFilled(null); setVoiceMsg(''); setVoiceState('idle');
      setJustCreated({ name: createdName, slot_number: slot, announced });
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || '빠른 등록 실패'); return false; }
    finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="quick-add-modal">
      <div className="bg-white dark:bg-[#141414] rounded-t-2xl sm:rounded-2xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-black mb-3 inline-flex items-center gap-2"><Zap size={16} className="text-[#FF3B30]" /> 빠른 등록</h3>
        {/* STEP 6-VOICE-F · 등록 완료 상태 · Modal 유지 · [다음 상품 등록] / [닫기] · 음성 명령 지원 */}
        {justCreated ? (
          <div data-testid="quick-add-success-view">
            <div className="rounded-2xl border border-emerald-400/50 bg-emerald-50 dark:bg-emerald-900/20 p-4 mb-4" data-testid="quick-add-success-banner">
              <div className="flex items-center gap-2 mb-2">
                <Check size={18} className="text-emerald-600" />
                <span className="text-sm font-black text-emerald-700 dark:text-emerald-300">상품 등록 완료</span>
              </div>
              <div className="grid grid-cols-[70px_1fr] gap-x-3 gap-y-1 text-xs">
                <span className="text-gray-500">상품명</span>
                <span className="font-bold" data-testid="quick-add-success-name">{justCreated.name}</span>
                <span className="text-gray-500">상품번호</span>
                <span className="font-black text-[#FF3B30]" data-testid="quick-add-success-slot">{justCreated.slot_number}번</span>
                {justCreated.announced && (
                  <>
                    <span className="text-gray-500">상태</span>
                    <span className="font-bold text-emerald-700 dark:text-emerald-300" data-testid="quick-add-success-announced">📢 지금 소개 상품 지정</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex gap-2 mb-3">
              <button onClick={handleNext}
                className="flex-1 py-3 rounded-full bg-[#FF3B30] text-white text-sm font-black inline-flex items-center justify-center gap-1"
                data-testid="quick-add-next-btn">
                <Plus size={14} /> 다음 상품 등록
              </button>
              <button onClick={handleClose}
                className="flex-1 py-3 rounded-full border border-black/10 dark:border-white/10 text-sm font-bold"
                data-testid="quick-add-done-btn">
                닫기
              </button>
            </div>
            {/* 음성 명령 (done state · "다음 상품" / "닫기") */}
            <div className="rounded-xl border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.03] p-3 flex items-center gap-2 flex-wrap" data-testid="quick-add-cmd-box-done">
              {cmdState === 'listening' ? (
                <button type="button" onClick={stopCmd}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-black bg-[#FF3B30] text-white animate-pulse"
                  data-testid="quick-add-cmd-stop-done">
                  <Square size={12} fill="white" /> 명령 완료
                </button>
              ) : cmdState === 'processing' ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold bg-black/10 dark:bg-white/10" data-testid="quick-add-cmd-processing-done">
                  <Loader2 size={12} className="animate-spin" /> 명령 인식 중...
                </span>
              ) : (
                <button type="button" onClick={() => startCmd('done')}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-black bg-black text-white hover:bg-gray-800"
                  data-testid="quick-add-cmd-start-done">
                  <Mic size={12} /> 음성 명령
                </button>
              )}
              <span className="text-[10px] text-gray-500 flex-1 min-w-0" data-testid="quick-add-cmd-msg-done">
                {cmdMsg || '"다음 상품" 또는 "닫기"라고 말해주세요.'}
              </span>
            </div>
          </div>
        ) : (<>
        <p className="text-xs text-gray-500 mb-3">방송 중에 10초 안에 등록 가능한 최소 정보만 입력하세요.</p>

        {/* STEP 6-VOICE-C · 음성 입력 진입점 (기존 필드에 자동채움) */}
        <div className="mb-3 rounded-xl border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.03] p-3 flex items-center gap-2 flex-wrap" data-testid="quick-add-voice-box">
          {voiceState === 'recording' ? (
            <button type="button" onClick={stopVoice}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-black bg-[#FF3B30] text-white animate-pulse"
              data-testid="quick-add-voice-stop">
              <Square size={12} fill="white" /> 녹음 완료
            </button>
          ) : voiceState === 'processing' ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold bg-black/10 dark:bg-white/10" data-testid="quick-add-voice-processing">
              <Loader2 size={12} className="animate-spin" /> 음성 분석 중...
            </span>
          ) : (
            <button type="button" onClick={startVoice} disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-black bg-black text-white hover:bg-gray-800 disabled:opacity-50"
              data-testid="quick-add-voice-start">
              <Mic size={12} /> 음성으로 입력
            </button>
          )}
          <span className="text-[10px] text-gray-500 flex-1 min-w-0" data-testid="quick-add-voice-msg">
            {voiceState === 'recording'
              ? '예: "여름 원피스 29000원 블랙 화이트 재고 20개" — 최대 15초'
              : (voiceMsg || '버튼을 누르고 상품명·가격·재고·옵션을 짧게 말해주세요.')}
          </span>
        </div>

        {/* STEP 6-VOICE-I · 자연어 예시 안내 (편안한 발화 유도) */}
        {!voiceFilled && voiceState === 'idle' && (
          <div className="mb-3 rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-white/[0.02] p-3" data-testid="quick-add-voice-example">
            <p className="text-[11px] font-black text-gray-700 dark:text-gray-200 mb-1 inline-flex items-center gap-1">
              <Mic size={10} className="text-[#FF3B30]" /> 이렇게 편하게 말씀하세요
            </p>
            <p className="text-[11px] text-gray-700 dark:text-gray-300 leading-relaxed pl-4">
              "여름 린넨 원피스 2만9천원<br/>블랙 화이트 S M L 20개"
            </p>
            <p className="text-[10px] text-gray-500 mt-1.5 pl-4 leading-relaxed">
              상품명 · 판매가 · 옵션 · 재고 순으로 자연스럽게 말씀하시면 자동으로 입력됩니다.<br/>
              <span className="text-gray-400">※ 색상/사이즈/재고 등의 항목명을 반드시 말할 필요는 없습니다.</span><br/>
              <span className="text-gray-400">※ 재고를 말하지 않으면 무제한으로 등록됩니다.</span>
            </p>
          </div>
        )}

        {/* STEP 6-VOICE-D · 음성 자동채움 결과 요약 (인식된 항목만 표시 · 임의 생성 없음) */}
        {voiceFilled && (
          <div className="mb-3 rounded-xl border border-emerald-400/40 bg-emerald-50 dark:bg-emerald-900/20 p-3" data-testid="quick-add-voice-summary">
            <p className="text-[11px] font-black text-emerald-700 dark:text-emerald-300 mb-2 inline-flex items-center gap-1">
              <Check size={12} /> 음성 입력 완료 — 내용을 확인한 후 등록해주세요
            </p>
            <dl className="grid grid-cols-[70px_1fr] gap-x-3 gap-y-1 text-[11px]">
              <dt className="text-gray-500">상품명</dt>
              <dd className="font-bold" data-testid="quick-add-voice-summary-name">{voiceFilled.name || <span className="text-gray-400 font-normal">미입력</span>}</dd>
              <dt className="text-gray-500">판매가</dt>
              <dd className="font-black text-[#FF3B30]" data-testid="quick-add-voice-summary-price">
                {voiceFilled.sale_price != null ? `${Number(voiceFilled.sale_price).toLocaleString()}원` : <span className="text-gray-400 font-normal">미입력</span>}
              </dd>
              <dt className="text-gray-500">재고</dt>
              <dd className="font-bold" data-testid="quick-add-voice-summary-stock">{voiceFilled.stock != null ? `${voiceFilled.stock}개` : <span className="text-gray-400 font-normal">무제한 (재고 미입력)</span>}</dd>
              <dt className="text-gray-500">옵션</dt>
              <dd className="font-bold" data-testid="quick-add-voice-summary-options">
                {Array.isArray(voiceFilled.option_groups) && voiceFilled.option_groups.length > 0 ? (
                  <span className="space-y-0.5 inline-block">
                    {voiceFilled.option_groups.map((g, i) => (
                      <span key={i} className="block" data-testid={`quick-add-voice-summary-group-${i}`}>
                        <span className="text-gray-500 font-normal mr-1">{g.name}</span>
                        {(g.values || []).join(' / ')}
                      </span>
                    ))}
                  </span>
                ) : Array.isArray(voiceFilled.options) && voiceFilled.options.length > 0
                  ? voiceFilled.options.join(' · ')
                  : <span className="text-gray-400 font-normal">없음</span>}
              </dd>
            </dl>
            {/* STEP 6-VOICE-F · 음성 명령 (confirmation state · "등록"/"등록하고 소개"/"등록만"/"취소") */}
            <div className="mt-3 pt-2 border-t border-emerald-400/30 flex items-center gap-2 flex-wrap" data-testid="quick-add-cmd-box-confirm">
              {cmdState === 'listening' ? (
                <button type="button" onClick={stopCmd}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-black bg-[#FF3B30] text-white animate-pulse"
                  data-testid="quick-add-cmd-stop-confirm">
                  <Square size={10} fill="white" /> 명령 완료
                </button>
              ) : cmdState === 'processing' ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold bg-black/10 dark:bg-white/10" data-testid="quick-add-cmd-processing-confirm">
                  <Loader2 size={10} className="animate-spin" /> 명령 인식 중...
                </span>
              ) : (
                <button type="button" onClick={() => startCmd('confirm')} disabled={busy}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-black bg-black text-white hover:bg-gray-800 disabled:opacity-50"
                  data-testid="quick-add-cmd-start-confirm">
                  <Mic size={10} /> 음성 명령
                </button>
              )}
              <span className="text-[10px] text-gray-600 dark:text-gray-400 flex-1 min-w-0" data-testid="quick-add-cmd-msg-confirm">
                {cmdMsg || '"등록" · "등록하고 소개" · "등록만" · "취소"'}
              </span>
            </div>
          </div>
        )}

        <label className="block text-xs font-bold mb-1">대표 이미지 <span className="text-gray-400 font-normal">(선택)</span></label>
        <div className="flex items-center gap-3 mb-2">
          {imgPreview ? (
            <div className="relative">
              <img src={imgPreview} alt="" className="w-16 h-16 rounded-lg object-cover" data-testid="quick-add-image-preview" />
              <button type="button" onClick={() => { setImgFile(null); setImgPreview(''); }}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black text-white flex items-center justify-center shadow"
                data-testid="quick-add-image-clear" aria-label="이미지 제거">
                <X size={10} />
              </button>
            </div>
          ) : (
            <div className="w-16 h-16 rounded-lg bg-black/5 dark:bg-white/5 flex items-center justify-center text-[10px] text-gray-400">미리보기</div>
          )}
          <div className="flex-1 flex flex-col gap-1.5 sm:flex-row sm:gap-2">
            {/* STEP 6-VOICE-B · 사진 업로드 (갤러리/파일) — 기존 accept="image/*" 유지, capture 없음 */}
            <label htmlFor="quick-add-image-file"
              className="cursor-pointer inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border border-black/10 dark:border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30]"
              data-testid="quick-add-image-upload-btn">
              <Upload size={12} /> 사진 업로드
            </label>
            <input id="quick-add-image-file" type="file" accept="image/*" className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setImgFile(f);
                setImgPreview(URL.createObjectURL(f));
                e.target.value = '';
              }} data-testid="quick-add-image" />
            {/* STEP 6-VOICE-B · 바로 촬영 · capture="environment" — 모바일 후면 카메라 즉시 호출.
                PC 는 capture 를 무시하고 일반 파일 선택기로 동작 → PC 무영향. */}
            <label htmlFor="quick-add-image-capture"
              className="cursor-pointer inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-black text-white hover:bg-gray-800"
              data-testid="quick-add-image-camera-btn">
              <Camera size={12} /> 바로 촬영
            </label>
            <input id="quick-add-image-capture" type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setImgFile(f);
                setImgPreview(URL.createObjectURL(f));
                e.target.value = '';
              }} data-testid="quick-add-image-capture" />
          </div>
        </div>
        <input value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="또는 이미지 URL 붙여넣기 (선택)"
          className="w-full px-3 py-2 border border-black/10 dark:border-white/10 rounded-lg text-xs bg-transparent mb-3" data-testid="quick-add-image-url" />

        <label className="block text-xs font-bold mb-1">상품명 <span className="text-[#FF3B30]">*</span></label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="예: 여름 니트"
          className="w-full px-3 py-2 border border-black/10 dark:border-white/10 rounded-lg text-sm bg-transparent mb-3" data-testid="quick-add-name" />

        <label className="block text-xs font-bold mb-1">판매가 (원) <span className="text-[#FF3B30]">*</span></label>
        <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="29000"
          className={`w-full px-3 py-2 border rounded-lg text-sm bg-transparent ${voiceFilled ? 'border-[#FF3B30]/60 mb-1' : 'border-black/10 dark:border-white/10 mb-3'}`} data-testid="quick-add-price" />
        {/* STEP 6-VOICE-D · 가격 확인 hint (음성입력 사용 시에만 노출 · 강한 경고 X) */}
        {voiceFilled && (
          <p className="text-[10px] text-[#FF3B30] mb-3" data-testid="quick-add-price-hint">
            AI 음성입력 · 가격을 확인해주세요.
          </p>
        )}

        <label className="block text-xs font-bold mb-1">재고 (선택)</label>
        <input type="number" value={stock} onChange={(e) => setStock(e.target.value)} placeholder="숫자 미입력 시 무제한"
          className="w-full px-3 py-2 border border-black/10 dark:border-white/10 rounded-lg text-sm bg-transparent mb-3" data-testid="quick-add-stock" />

        {/* Options — collapsed by default. */}
        <label className="inline-flex items-center gap-2 text-xs font-bold mb-3">
          <input type="checkbox" checked={hasOptions} onChange={(e) => setHasOptions(e.target.checked)} data-testid="quick-add-has-options" />
          옵션 있음
        </label>
        {hasOptions && (
          <div className="mb-3 space-y-3 rounded-xl border border-black/10 dark:border-white/10 p-3 bg-black/[0.02] dark:bg-white/[0.02]" data-testid="quick-add-options">
            {optionGroups.map((g, gi) => (
              <div key={gi} className="space-y-2" data-testid={`quick-add-group-${gi}`}>
                <div className="flex items-center gap-2">
                  <input value={g.name} onChange={(e) => setGroupName(gi, e.target.value)}
                    placeholder="옵션명 (예: 색상)"
                    className="flex-1 px-2 py-1.5 border border-black/10 dark:border-white/10 rounded text-xs bg-transparent" data-testid={`quick-add-group-name-${gi}`} />
                  {optionGroups.length > 1 && (
                    <button onClick={() => removeGroup(gi)} className="text-[10px] text-red-500 font-bold" data-testid={`quick-add-remove-group-${gi}`}>그룹 삭제</button>
                  )}
                </div>
                <ul className="space-y-1">
                  {g.values.map((v, vi) => (
                    <li key={vi} className="flex items-center gap-1" data-testid={`quick-add-value-${gi}-${vi}`}>
                      <input value={v.label} onChange={(e) => setValue(gi, vi, 'label', e.target.value)}
                        placeholder="옵션값 (예: 블랙)"
                        className="flex-1 px-2 py-1.5 border border-black/10 dark:border-white/10 rounded text-xs bg-transparent" />
                      <span className="text-[10px] text-gray-400 whitespace-nowrap">추가금액</span>
                      <input type="number" value={v.extra_price} onChange={(e) => setValue(gi, vi, 'extra_price', e.target.value)}
                        className="w-20 px-2 py-1.5 border border-black/10 dark:border-white/10 rounded text-xs bg-transparent text-right" />
                      {g.values.length > 1 && (
                        <button onClick={() => removeValue(gi, vi)} className="p-1 text-red-500" aria-label="삭제"><X size={12} /></button>
                      )}
                    </li>
                  ))}
                </ul>
                <button onClick={() => addValue(gi)}
                  className="text-[11px] font-bold text-[#FF3B30] inline-flex items-center gap-1"
                  data-testid={`quick-add-add-value-${gi}`}>+ 옵션값 추가</button>
              </div>
            ))}
            {optionGroups.length < 2 && (
              <button onClick={addGroup}
                className="w-full py-1.5 rounded-lg border border-dashed border-black/10 dark:border-white/10 text-[11px] font-bold text-gray-600 dark:text-gray-300"
                data-testid="quick-add-add-group">+ 옵션 그룹 추가 (최대 2)</button>
            )}
            <p className="text-[10px] text-gray-400">옵션별 재고와 옵션 조합별 가격은 등록 후 <b>상품관리</b>에서 상세 편집할 수 있습니다.</p>
          </div>
        )}

        <label className="inline-flex items-center gap-2 text-xs font-bold mb-4">
          <input type="checkbox" checked={announceNow} onChange={(e) => setAnnounceNow(e.target.checked)} data-testid="quick-add-announce" />
          등록 후 바로 소개중으로 지정
        </label>

        <div className="flex gap-2">
          <button disabled={busy} onClick={() => submit()}
            className="flex-1 py-2.5 rounded-full bg-[#FF3B30] text-white text-sm font-bold disabled:opacity-40 inline-flex items-center justify-center gap-1"
            data-testid="quick-add-submit"><Zap size={12} /> {busy ? '등록 중…' : '라이브 상품으로 등록'}</button>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-full border border-black/10 dark:border-white/10 text-sm font-bold">취소</button>
        </div>
        </>)}
      </div>
    </div>
  );
};

export default QuickAddModal;
